import React, { Fragment, useEffect, useState } from 'react';
import T from 'prop-types';
// redux
import { setPageOptions } from 'reducers/layout';
import { setBreadcrumbs } from 'reducers/breadcrumbs';
// components
import { FormattedMessage } from 'react-intl';
import Preloader from 'components/Preloader';
import TreatmentProtocolForm from '../../components/TreatmentProtocolForm';
// constants
import { defaultApplicablePhases } from '../../components/TreatmentProtocolForm/helper';
// endpoints
import { createFarmTreatmentProtocol } from 'endpoints/treatmentProtocols';
// utils
import { connect } from 'react-redux';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { formatTreatmentProtocolPayload } from '../../helper';

const initialTreatment = { product: '', steps: [{}], };
const defaultInitialValues = { treatments: [initialTreatment], applicable_phases: defaultApplicablePhases };

const CreateFarmTreatmentProtocol = ({ farm, setBreadcrumbs, setPageOptions, router, isABFEnabled, location }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { params: { farmId }, push } = router;
  const { returnUrl } = location.query || {};

  useEffect(() => {
    // Set back link based on whether we have a return URL
    const backLink = returnUrl || `/treatment-protocols-farms/${farmId}/treatment-protocols`;
    setPageOptions({ backLink });
  }, [returnUrl, farmId]);

  useEffect(() => {
    setBreadcrumbs([
      {
        label: <FormattedMessage id="general.treatmentProtocolFarms" />,
        path: '/treatment-protocols-farms',
      },
      {
        label: farm?.name || '',
        path: `/treatment-protocols-farms/${farmId}/treatment-protocols`,
      },
      {
        label: <FormattedMessage id="general.createTreatmentProtocol" />,
        useLabelAsMobileTitle: true,
      },
    ]);
  }, [farm?.name, farmId]);

  const onSubmit = async (newProtocolData) => {
    try {
      setIsLoading(true);
      const treatmentProtocolPayload = formatTreatmentProtocolPayload(newProtocolData);
      await createFarmTreatmentProtocol(farmId, treatmentProtocolPayload);

      // Pass refresh state in location
      push({
        pathname: returnUrl || `/treatment-protocols-farms/${farmId}/treatment-protocols`,
        state: { shouldRefresh: true }
      });
    } catch (error) {
      toastResponseErrors(error);
      setIsLoading(false);
    }
  };

  return (
    <Fragment>
      <Preloader isActive={isLoading} />
      <TreatmentProtocolForm
        isCreate
        isPhasesApplicable={farm?.farm_type === 'su'}
        isEffectiveDatesApplicable
        onSubmit={onSubmit}
        initialValues={defaultInitialValues}
        title={<FormattedMessage id="general.createTreatmentProtocol" />}
        submitLabel={<FormattedMessage id="general.createTreatmentProtocol" />}
        ABFEnabled={isABFEnabled}
        createScopeNotice={(
          <FormattedMessage
            id="component.treatmentProtocols.createFarmScopeWarning"
            values={{
              w: (chunks) => <b>{chunks}</b>,
              b: (chunks) => <b>{chunks}</b>,
            }}
          />
        )}
      />
    </Fragment>
  );
};

CreateFarmTreatmentProtocol.propTypes = {
  farm: T.object.isRequired,
  setBreadcrumbs: T.func.isRequired,
  setPageOptions: T.func.isRequired,
  router: T.object.isRequired,
  isABFEnabled: T.bool.isRequired,
  location: T.object.isRequired,
};

export default connect(
  (state) => ({
    farm: state.profiles.farm.data,
    isABFEnabled: state.auth.user.current_company.abf_tracking,
  }), {
    setPageOptions,
    setBreadcrumbs,
  }
)(CreateFarmTreatmentProtocol);
